import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../api";
import { WORLD_DOTS, projectLngLat } from "../lib/worldDots";

const W = 420;
const H = 240;

export default function Radar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/analytics/radar"); setData(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const px = (lng, lat) => {
    const p = projectLngLat(lng, lat);
    return { cx: p.x * W, cy: p.y * H };
  };

  const pins = data?.pins || [];
  const max = Math.max(1, ...pins.map((p) => p.count));

  return (
    <div className="px-6 pt-4 pb-4" data-testid="radar-screen">
      <p className="text-[12px] tracking-[0.16em] uppercase text-ink-500">Where Aarya lives</p>
      <h1 className="font-display text-[34px] leading-[1.05] text-ink-900 mt-1 tracking-tight">Aarya Radar</h1>
      <p className="text-sm text-ink-500 mt-1">An anonymous, aggregated map of every place Aarya has been activated.</p>

      {/* Stat cards */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-sand-200 p-4" data-testid="radar-total">
          <p className="text-[11px] tracking-[0.16em] uppercase text-ink-500">Total activations</p>
          <p className="font-display text-3xl text-ink-900 mt-1">{loading ? "…" : (data?.total_users ?? 0)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-sand-200 p-4" data-testid="radar-countries">
          <p className="text-[11px] tracking-[0.16em] uppercase text-ink-500">Countries reached</p>
          <p className="font-display text-3xl text-ink-900 mt-1">{loading ? "…" : (data?.countries?.length ?? 0)}</p>
        </div>
      </div>

      {/* World map */}
      <div className="mt-5 relative rounded-3xl bg-ink-900 text-white p-4 overflow-hidden shadow-lifted" data-testid="radar-map">
        <div className="absolute inset-0 grain opacity-60"/>
        <p className="text-[11px] tracking-[0.18em] uppercase text-white/60">Live · Mock data</p>
        <p className="font-display text-xl mt-0.5">Aarya, around the world</p>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-3" preserveAspectRatio="xMidYMid meet">
          {/* base dot grid (continents traced) */}
          {WORLD_DOTS.map(([lng, lat], i) => {
            const { cx, cy } = px(lng, lat);
            return <circle key={i} cx={cx} cy={cy} r={1.5} fill="rgba(255,255,255,0.18)"/>;
          })}
          {/* user pins */}
          {pins.map((p, i) => {
            const { cx, cy } = px(p.lng, p.lat);
            const r = 4 + (p.count / max) * 8;
            return (
              <g key={i}>
                <motion.circle
                  cx={cx} cy={cy} r={r + 6}
                  fill="rgba(200,124,91,0.18)"
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: [0.6, 0.2, 0.6], scale: [0.9, 1.2, 0.9] }}
                  transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.1 }}
                />
                <circle cx={cx} cy={cy} r={r} fill="#C87C5B" stroke="white" strokeWidth="1.5"/>
              </g>
            );
          })}
        </svg>
        {pins.length === 0 && !loading && (
          <p className="text-[12px] text-white/60 mt-2">No activations to plot yet. Be the first.</p>
        )}
      </div>

      {/* Top countries */}
      <div className="mt-5">
        <p className="text-[11px] tracking-[0.16em] uppercase text-ink-500 font-semibold">Top countries</p>
        {loading && <p className="text-sm text-ink-500 mt-2" data-testid="radar-loading">Loading…</p>}
        {!loading && (data?.countries?.length || 0) === 0 && (
          <p className="text-sm text-ink-500 mt-2">No data yet.</p>
        )}
        <div className="mt-2 space-y-2">
          {(data?.countries || []).slice(0, 8).map((c) => {
            const total = data?.total_users || 1;
            const pct = Math.max(4, Math.round((c.count / total) * 100));
            return (
              <div key={c.code} className="bg-white rounded-2xl border border-sand-200 p-3" data-testid={`radar-country-${c.code}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[14px] text-ink-900 font-semibold">{c.name}</span>
                  <span className="text-[12px] text-ink-500">{c.count} {c.count === 1 ? "user" : "users"}</span>
                </div>
                <div className="mt-2 h-1.5 bg-sand-100 rounded-full overflow-hidden">
                  <div className="h-full bg-terracotta-500 rounded-full" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-center text-[11px] text-ink-500 mt-6">
        🇮🇳 Aggregated · anonymous · for IndoBlitz research only
      </p>
    </div>
  );
}
